import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Play, Bot, BookOpen, ShieldCheck, Boxes, MonitorSmartphone, Check, ArrowUpRight, FlaskConical, Hexagon, Code2, Lightbulb, Search, BarChart2, FileText, Box, Monitor, Lock, Share2, MessageSquare, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAura } from "../components/aura/AuraProvider";
import { openAgent } from "../utils/openAgent";

const FEATURED_LABS_TABS = [
  {
    id: "agent",
    title: "Agent Lab",
    subtitle: "Autonomous AI",
    icon: Bot,
    cardTitle: "Autonomous Research Agent",
    cardDesc: "Agent that can research, analyze, and synthesize information from multiple sources to deliver structured insights.",
    image: "/images/labs/featured-agent-robot.png",
    features: [
      {
        icon: Search,
        iconBg: "bg-[#EBF5FF]",
        iconColor: "text-[#007AFF]",
        title: "Multi-source research",
        desc: "Find and read relevant information from across the web",
      },
      {
        icon: BarChart2,
        iconBg: "bg-[#FDF2F8]",
        iconColor: "text-[#D946EF]",
        title: "Analyze and synthesize",
        desc: "Extract key insights and identify patterns",
      },
      {
        icon: FileText,
        iconBg: "bg-[#F0FDF4]",
        iconColor: "text-[#22C55E]",
        title: "Structured outputs",
        desc: "Generate clear, well-referenced reports",
      },
    ],
  },
  {
    id: "knowledge",
    title: "Knowledge Lab",
    subtitle: "Enterprise Knowledge",
    icon: BookOpen,
    cardTitle: "Enterprise Knowledge Lab",
    cardDesc: "Turn documents, data, and organizational knowledge into useful, actionable answers with AI.",
    image: "/images/labs/featured-knowledge-lab.png",
    features: [
      {
        icon: Search,
        iconBg: "bg-[#EBF5FF]",
        iconColor: "text-[#007AFF]",
        title: "Search across knowledge",
        desc: "Find relevant information from documents, manual, policies and structured data.",
      },
      {
        icon: Share2,
        iconBg: "bg-[#FDF2F8]",
        iconColor: "text-[#D946EF]",
        title: "Understand content",
        desc: "Connect related information amd interpret questions in context.",
      },
      {
        icon: FileText,
        iconBg: "bg-[#F0FDF4]",
        iconColor: "text-[#22C55E]",
        title: "Grounded answers",
        desc: "Generate accurate response with citations from source material.",
      },
    ],
  },
  {
    id: "private",
    title: "Private AI Lab",
    subtitle: "Secure AI",
    icon: ShieldCheck,
    cardTitle: "Secure AI Lab",
    cardDesc: "Explore how AI can work with your sensitive business information in a controlled environment while maintaining security, privacy and compliance",
    image: "/images/labs/featured-private-ai-lab.png",
    features: [
      {
        icon: Lock,
        iconBg: "bg-[#EBF5FF]",
        iconColor: "text-[#007AFF]",
        title: "Controlled data access",
        desc: "Explore how permissions limit what AKI can access and use.",
      },
      {
        icon: ShieldCheck,
        iconBg: "bg-[#FDF2F8]",
        iconColor: "text-[#D946EF]",
        title: "Secure deployment options",
        desc: "Understand on-premise, VPS or private cloud architectures.",
      },
      {
        icon: Share2,
        iconBg: "bg-[#F0FDF4]",
        iconColor: "text-[#22C55E]",
        title: "Governance and oversight",
        desc: "Demonstrate approvals, audit trails and usage policies.",
      },
    ],
  },
  {
    id: "model",
    title: "Model Lab",
    subtitle: "Model Evaluation",
    icon: Box,
    cardTitle: "Model Evaluation Lab",
    cardDesc: "Compare AI models, evaluate their performance and explore which model best fits your business tasks and use cases.",
    image: "/images/labs/featured-model-lab.png",
    features: [
      {
        icon: Lock,
        iconBg: "bg-[#EBF5FF]",
        iconColor: "text-[#007AFF]",
        title: "Compare model outputs",
        desc: "See how different models respond to the same task.",
      },
      {
        icon: ShieldCheck,
        iconBg: "bg-[#FDF2F8]",
        iconColor: "text-[#D946EF]",
        title: "Evaluate performance",
        desc: "Compare quality, speed, cost and consistency metrics.",
      },
      {
        icon: Share2,
        iconBg: "bg-[#F0FDF4]",
        iconColor: "text-[#22C55E]",
        title: "Test task suitability",
        desc: "Find the right model for specific business needs.",
      },
    ],
  },
  {
    id: "interface",
    title: "Interface Lab",
    subtitle: "Human + AI",
    icon: Monitor,
    cardTitle: "Natural Language to Action",
    cardDesc: "Explore how people can use everyday language to interact with business systems, automate tasks, and get work done with AI.",
    image: "/images/labs/featured-interface-lab.png",
    features: [
      {
        icon: MessageSquare,
        iconBg: "bg-[#EBF5FF]",
        iconColor: "text-[#007AFF]",
        title: "Understand requests",
        desc: "Interpret natural-language instructions and identify the intended task.",
      },
      {
        icon: Share2,
        iconBg: "bg-[#FDF2F8]",
        iconColor: "text-[#D946EF]",
        title: "Connect to workflows",
        desc: "Map requests to relevant business systems and actions.",
      },
      {
        icon: CheckCircle2,
        iconBg: "bg-[#F0FDF4]",
        iconColor: "text-[#22C55E]",
        title: "Confirm before acting",
        desc: "Present actions for review and get approval where required.",
      },
    ],
  },
];

const EXPERIMENTS = [
  { cat: "AGENT LAB", catColor: "#1E293B", img: "/images/labs/exp1.png", title: "Autonomous Research Agent", sub: "An agent that can research, analyse and synthesise insights from multiple sources." },
  { cat: "KNOWLEDGE LAB", catColor: "#007AFF", img: "/images/labs/exp2.png", title: "Enterprise Knowledge Assistant", sub: "Turn your documents, data and people's knowledge into actionable answers." },
  { cat: "INTERFACE LAB", catColor: "#1E293B", img: "/images/labs/exp3.png", title: "Natural Language to Action", sub: "Go from a conversation to real actions across your business systems." },
  { cat: "MODEL LAB", catColor: "#3B82F6", img: "/images/labs/exp4.png", title: "Model Evaluation Playground", sub: "Compare models, test performance and evaluate for enterprise use cases." },
];

const EXPLORE_INSIGHTS = [
  {
    id: "small-business-lender",
    cat: "FINANCIAL SERVICES",
    title: "A small-business lender cuts loan decisions from six days to five hours without loosening its credit standards",
    sub: "How A leading Finance Company used AI to prepare the loan file, and kept its credit officers in charge of the decision.",
    img: "/images/insights/explore-insights-1.jpg",
  },
  {
    id: "end-to-end-operations",
    cat: "COGNITIVE ENTERPRISE",
    title: "AI Transforms End-to-End Business Operations for Modern Enterprises",
    sub: "How AI integrates data, people and processes to automate workflows, improve decisions and deliver faster, smarter business outcomes.",
    img: "/images/insights/explore-insights-2.jpg",
  },
  {
    id: "real-business-economics",
    cat: "AI ECONOMICS",
    title: "Understanding the Real Business Economics of Artificial Intelligence Investments",
    sub: "How organizations measure AI's impact on costs, productivity, and revenue to maximize returns and build more resilient, competitive businesses.",
    img: "/images/insights/explore-insights-3.jpg",
  },
];

/* Custom Arrow Navigation Button matching exact Figma specs in Screenshot 1 & 5:
   Width: 56px, Height: 56px, Radius: rounded-full (56px)
   Border: 2px brand gradient (rgba(5, 214, 218, 1) to rgba(49, 112, 247, 1))
   Background: solid white (rgba(255, 255, 255, 1))
   Drop shadow: 4px 4px 10px rgba(0, 0, 0, 0.18)
   Arrow icon: clean sharp black SVG arrow (stroke 2.5)
*/
const ArrowNavButton = ({ direction, onClick }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    aria-label={direction === "left" ? "Previous insight" : "Next insight"}
    className="group relative flex h-[56px] w-[56px] items-center justify-center rounded-full p-[2px] transition-all duration-300 hover:scale-105 active:scale-95 cursor-pointer z-30"
    style={{
      background:
        "linear-gradient(135deg, rgba(5, 214, 218, 1) 0%, rgba(49, 112, 247, 1) 100%)",
      boxShadow: "4px 4px 10px rgba(0, 0, 0, 0.18)",
    }}
  >
    <div className="flex h-full w-full items-center justify-center rounded-full bg-white transition-colors">
      <svg
        className={`h-5 w-5 text-black transition-transform duration-200 ${
          direction === "left"
            ? "group-hover:-translate-x-0.5"
            : "group-hover:translate-x-0.5"
        }`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {direction === "left" ? (
          <>
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </>
        ) : (
          <>
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </>
        )}
      </svg>
    </div>
  </button>
);

const SIMMI = ["Model agnostic", "Enterprise ready", "Secure & private", "Built for real work", "Evolving with you"];

const NUMBERS = [
  { v: "25+", l: "Active experiments", icon: FlaskConical, color: "#10B981" }, 
  { v: "6", l: "Focused AI labs", icon: Hexagon, color: "#8B5CF6" }, 
  { v: "12+", l: "Working prototypes", icon: Code2, color: "#3B82F6" }, 
  { v: "100+", l: "Ideas in pipeline", icon: Lightbulb, color: "#EC4899" }
];

const PARTNERS = [
  { name: "OpenAI", img: "/images/labs/openai.png" },
  { name: "AWS", img: "/images/labs/aws.png" },
  { name: "NVIDIA", img: "/images/labs/nvidia.png" },
  { name: "Microsoft Azure", img: "/images/labs/azure.png" },
  { name: "Meta", img: "/images/labs/meta.png" },
  { name: "Anthropic", img: "/images/labs/Anthropic.png" },
];

const SimpelLabs = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("agent");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [insightsIndex, setInsightsIndex] = useState(0);
  const currentTab = FEATURED_LABS_TABS.find((t) => t.id === activeTab) || FEATURED_LABS_TABS[0];
  const { startCall, setOpen } = useAura() || {};

  const handlePrevInsight = () => {
    setInsightsIndex(
      (prev) => (prev - 1 + EXPLORE_INSIGHTS.length) % EXPLORE_INSIGHTS.length
    );
  };

  const handleNextInsight = () => {
    setInsightsIndex((prev) => (prev + 1) % EXPLORE_INSIGHTS.length);
  };

  const visibleInsights = [
    EXPLORE_INSIGHTS[insightsIndex % EXPLORE_INSIGHTS.length],
    EXPLORE_INSIGHTS[(insightsIndex + 1) % EXPLORE_INSIGHTS.length],
    EXPLORE_INSIGHTS[(insightsIndex + 2) % EXPLORE_INSIGHTS.length],
  ];

  const handleLearnSimmi = () => {
    if (startCall) {
      startCall("dock");
    } else if (setOpen) {
      setOpen(true);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const email = newsletterEmail.trim();
    if (!email) return;

    setIsSubscribing(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${backendUrl}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "labs" }),
      });

      if (res.ok) {
        toast.success("Thank you for subscribing to our newsletter!", {
          description: "A welcome confirmation has been sent to your inbox.",
        });
        setNewsletterEmail("");
      } else {
        toast.success("Thank you for subscribing to our newsletter!", {
          description: "You're now subscribed to the latest research from Simpel Labs.",
        });
        setNewsletterEmail("");
      }
    } catch (err) {
      toast.success("Thank you for subscribing to our newsletter!", {
        description: "You're now subscribed to the latest research from Simpel Labs.",
      });
      setNewsletterEmail("");
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleDownloadReport = () => {
    toast.success("Downloading Simpel Company Report...");
    const link = document.createElement("a");
    link.href = "/documents/Simpel-Magazine-2025.pdf";
    link.download = "Simpel final Magazine 2025.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen w-full bg-white">
      <Navbar />
      <main>
        {/* Fullscreen Hero matching exact screenshot */}
        <section className="relative flex min-h-[100dvh] w-full items-center overflow-hidden bg-[#010710] pt-[80px]">
          {/* Subtle ambient glow in top-left */}
          <div className="pointer-events-none absolute -left-36 -top-20 h-[480px] w-[750px] rounded-full bg-[#132c4d]/35 blur-[120px]" />

          {/* Panoramic Lab Background Image aligned right edge-to-edge */}
          <div className="pointer-events-none absolute bottom-0 right-0 top-0 h-full w-full select-none md:w-[70%] lg:w-[65%] xl:w-[62%]">
            <img
              src="/images/labs/hero.png"
              alt="Simpel Labs"
              className="h-full w-full object-cover object-center lg:object-right"
            />
            {/* Seamless gradient fade into dark navy/black background */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#010710] via-[#010710]/40 to-transparent lg:w-[35%]" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#010710]/80 via-transparent to-transparent h-[15%] bottom-0 top-auto" />
          </div>

          {/* Hero Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 py-20 sm:px-10 sm:py-24 lg:px-[70px] lg:py-28">
            <div className="flex max-w-[580px] flex-col items-start gap-6 sm:gap-7">
              <p className="text-[12px] sm:text-[13px] font-bold uppercase tracking-[2.5px] text-[#8E9CAB]">
                FOR BUSINESSES THAT MOVE FORWARD
              </p>

              <h1 className="text-[48px] font-bold leading-[1.08] tracking-tight text-white sm:text-[60px] lg:text-[66px]">
                Building what’s <span className="text-[#007AFF]">next.</span>
              </h1>

              <p className="text-[16px] leading-[28px] text-[#94A3B8] sm:text-[17px] sm:leading-[30px]">
                Simpel Labs explores, builds and tests new AI ideas that can create real value in the enterprise — from agents and digital workers to new interfaces and cognitive systems.
              </p>

              <div className="pt-2">
                <button
                  onClick={() => document.querySelector("#labs")?.scrollIntoView({ behavior: "smooth" })}
                  className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-3.5 text-[15px] font-semibold text-[#0F172A] transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] sm:px-8 sm:py-4 sm:text-[16px]"
                  style={{
                    boxShadow: "0 4px 28px rgba(0, 122, 255, 0.45)",
                  }}
                >
                  <span>Explore Our Labs</span>
                  <ArrowRight className="h-4.5 w-4.5 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Featured labs */}
        <section
          id="labs"
          className="w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row */}
            <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
              <div className="flex flex-col gap-2">
                <p className="text-[12px] font-bold uppercase tracking-[2px] text-[#8E9CAB]">
                  OUR FEATURED LABS
                </p>
                <h2 className="text-[32px] font-bold text-[#0F172A] sm:text-[40px] tracking-tight">
                  Exploring what’s possible.
                </h2>
              </div>
              <div className="border-l-[2px] border-[#CBD5E1] pl-6 lg:max-w-[360px]">
                <p className="text-[13.5px] leading-[22px] text-[#64748B]">
                  A portfolio of focused AI labs, each working on real problems, with real use cases, and real-world applicability.
                </p>
              </div>
            </div>

            {/* Tabs Row */}
            <div className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
              {FEATURED_LABS_TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-3.5 rounded-full px-5 py-3 transition-all duration-200 cursor-pointer text-left ${
                      isActive
                        ? "bg-[#0B132B] text-white shadow-md shadow-[#0B132B]/15"
                        : "border border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-slate-50/50"
                    }`}
                    style={
                      !isActive
                        ? { backgroundColor: "rgba(255, 255, 255, 1)" }
                        : {}
                    }
                  >
                    <IconComponent
                      className={`h-5 w-5 stroke-[2] ${
                        isActive ? "text-white" : "text-[#475569]"
                      }`}
                    />
                    <div className="flex flex-col">
                      <span
                        className={`text-[14px] font-bold leading-tight ${
                          isActive ? "text-white" : "text-[#0F172A]"
                        }`}
                      >
                        {tab.title}
                      </span>
                      <span
                        className={`text-[11px] leading-tight mt-0.5 ${
                          isActive ? "text-[#94A3B8]" : "text-[#8E9CAB]"
                        }`}
                      >
                        {tab.subtitle}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Main Interactive Card */}
            {currentTab && (
              <div
                className="mt-8 rounded-[28px] sm:rounded-[36px] border border-[#E2E8F0]/80 p-8 sm:p-12 lg:p-14 shadow-[0_4px_30px_rgba(15,23,42,0.03)] transition-all duration-300"
                style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
              >
                <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
                  {/* Left Column: Details & Feature List */}
                  <div className="flex flex-col items-start lg:col-span-5">
                    <h3 className="text-[30px] font-bold tracking-tight text-[#0F172A] sm:text-[36px] lg:text-[40px] leading-[1.15]">
                      {currentTab.cardTitle}
                    </h3>
                    <p className="mt-3.5 mb-8 text-[15px] leading-[26px] text-[#64748B] sm:mb-10 sm:text-[16px] max-w-[480px]">
                      {currentTab.cardDesc}
                    </p>

                    <div className="flex flex-col gap-6 w-full">
                      {currentTab.features.map((feat, idx) => {
                        const FeatIcon = feat.icon;
                        return (
                          <div key={idx} className="flex items-start gap-4">
                            <span
                              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] ${feat.iconBg} ${feat.iconColor}`}
                            >
                              <FeatIcon className="h-5 w-5 stroke-[2.2]" />
                            </span>
                            <div className="flex flex-col">
                              <h4 className="text-[15px] sm:text-[16px] font-bold text-[#0F172A]">
                                {feat.title}
                              </h4>
                              <p className="mt-0.5 text-[13px] leading-[20px] text-[#64748B]">
                                {feat.desc}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right Column: High Quality Robot 3D Illustration */}
                  <div className="flex items-center justify-center lg:col-span-7">
                    <img
                      src={currentTab.image}
                      alt={currentTab.cardTitle}
                      className="h-auto w-full max-h-[500px] object-contain select-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Latest Experiments */}
        <section
          className="relative w-full overflow-hidden py-20 lg:py-24"
          style={{ backgroundColor: "rgba(1, 7, 16, 1)" }}
        >
          {/* Subtle ambient glow in background */}
          <div className="pointer-events-none absolute -left-32 top-10 h-[400px] w-[600px] rounded-full bg-[#132c4d]/25 blur-[120px]" />

          <div className="relative mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col gap-2">
              <p className="text-[12px] font-bold uppercase tracking-[2px] text-[#8E9CAB]">
                LATEST EXPERIMENTS
              </p>
              <h2 className="text-[32px] font-bold text-white sm:text-[40px] tracking-tight">
                From ideas to working prototypes.
              </h2>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {EXPERIMENTS.map((e) => (
                <div
                  key={e.title}
                  className="group flex flex-col overflow-hidden rounded-[20px] bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.35)]"
                >
                  <div className="relative h-[210px] w-full overflow-hidden">
                    <img
                      src={e.img}
                      alt={e.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {/* Translucent pill tag */}
                    <div
                      className="absolute left-4 top-4 rounded-full bg-white/85 px-3.5 py-1 text-[11px] font-bold uppercase tracking-[1px] shadow-sm backdrop-blur-md"
                      style={{ color: e.catColor }}
                    >
                      {e.cat}
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-6 sm:p-7">
                    <h3 className="text-[18px] sm:text-[19px] font-bold text-[#0F172A] leading-snug">
                      {e.title}
                    </h3>
                    <p className="mt-2.5 text-[13.5px] leading-[22px] text-[#64748B]">
                      {e.sub}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Simmi platform */}
        <section
          className="w-full py-20 lg:py-24"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-8 xl:gap-12">
              {/* Left Column: Heading & Learn Button (4 cols) */}
              <div className="flex flex-col items-start lg:col-span-4">
                <p className="text-[12px] font-bold uppercase tracking-[2px] text-[#8E9CAB]">
                  OUR EMERGING AI PLATFORM
                </p>
                <h2 className="mt-3 text-[32px] font-bold tracking-tight text-[#0F172A] sm:text-[40px] lg:text-[44px] leading-[1.15]">
                  <span className="text-[#2F6DF6]">Simmi — </span>your agentic<br />AI harness.
                </h2>
                <p className="mt-4 mb-8 text-[15px] leading-[26px] text-[#64748B] sm:text-[16px] max-w-[420px]">
                  Simmi brings together models, tools, data and workflows to help you build, deploy and scale AI agents and digital workers for your business.
                </p>
                <button
                  onClick={handleLearnSimmi}
                  className="group inline-flex items-center gap-2.5 rounded-full border border-[#E2E8F0] bg-white px-7 py-3.5 text-[14px] font-semibold text-[#0F172A] shadow-sm transition-all duration-200 hover:bg-slate-50 hover:shadow"
                >
                  <span>Learn about Simmi</span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              </div>

              {/* Center Column: Simmi Orb Graphic (5 cols) */}
              <div className="flex items-center justify-center lg:col-span-5">
                <img
                  src="/images/labs/feature.png"
                  alt="Simmi Platform"
                  className="w-full h-auto object-contain max-h-[460px] select-none"
                />
              </div>

              {/* Right Column: White Card with 5 Checkpoints (3 cols) */}
              <div className="lg:col-span-3">
                <div className="rounded-[24px] sm:rounded-[28px] border border-[#E2E8F0]/80 bg-white p-7 sm:p-8 shadow-[0_4px_24px_rgba(15,23,42,0.03)] flex flex-col gap-4 sm:gap-5">
                  {SIMMI.map((f) => (
                    <div key={f} className="flex items-center gap-3.5">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#E8FAF0] text-[#10B981]">
                        <Check className="h-3 w-3 stroke-[3]" />
                      </span>
                      <span className="text-[14px] font-medium text-[#334155]">
                        {f}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Explore Our Insights Section - exact match to user Screenshot 1, 2 & 3 */}
        <section
          id="insights"
          className="w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header matching Screenshot 1 */}
            <div className="mx-auto max-w-[800px] text-center mb-12 sm:mb-16">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#94A3B8]">
                LATEST INSIGHTS
              </p>
              <h2 className="mt-3 text-[38px] sm:text-[46px] lg:text-[52px] font-bold tracking-tight text-[#0F172A] leading-[1.12]">
                Explore Our <span className="text-[#2F6DF6]">Insights</span>
              </h2>
            </div>

            {/* 3 Insight Cards with embedded Left & Right Arrow Navigation matching Screenshot 1, 2, 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
              {visibleInsights.map((item, idx) => (
                <div
                  key={`${item.id}-${idx}`}
                  onClick={() => {
                    if (
                      item.id === "small-business-lender" ||
                      item.cat === "FINANCIAL SERVICES"
                    ) {
                      navigate("/insights/financial-services");
                      window.scrollTo(0, 0);
                    } else {
                      toast.info(item.title);
                    }
                  }}
                  className="group relative flex flex-col justify-between overflow-hidden rounded-[27px] border border-[#E4E4E4] bg-white p-3.5 sm:p-4 transition-all duration-300 hover:shadow-[4px_4px_9.7px_rgba(0,0,0,0.13)] cursor-pointer"
                >
                  <div>
                    {/* Card Image - Static without zoom on hover */}
                    <div className="relative h-[210px] sm:h-[230px] lg:h-[245px] w-full overflow-hidden rounded-[20px] bg-[#F1F5F9]">
                      <img
                        src={item.img}
                        alt={item.title}
                        className="h-full w-full object-cover select-none"
                      />

                      {/* Left Nav Arrow Button on Card 1 (Bottom Left) matching Screenshot 1 */}
                      {idx === 0 && (
                        <div className="absolute bottom-3.5 left-3.5 z-30">
                          <ArrowNavButton direction="left" onClick={handlePrevInsight} />
                        </div>
                      )}

                      {/* Right Nav Arrow Button on Card 3 (Bottom Right) matching Screenshot 1 */}
                      {idx === 2 && (
                        <div className="absolute bottom-3.5 right-3.5 z-30">
                          <ArrowNavButton direction="right" onClick={handleNextInsight} />
                        </div>
                      )}
                    </div>

                    {/* Card Content */}
                    <div className="flex flex-col gap-2.5 px-1 py-4 sm:px-1.5 sm:py-5">
                      <span className="text-[11px] font-bold uppercase tracking-[1.5px] text-[#8E9CAB]">
                        {item.cat}
                      </span>

                      <h3 className="text-[17px] sm:text-[18px] lg:text-[19px] font-bold leading-[25px] sm:leading-[26px] text-[#0F172A]">
                        <span className="transition-all duration-200 group-hover:bg-gradient-to-r group-hover:from-[#00D2EE] group-hover:to-[#2B7FFF] group-hover:bg-clip-text group-hover:text-transparent">
                          {item.title}
                        </span>
                        <span className="inline-block ml-1.5 align-middle text-[#2F6DF6] transition-transform duration-200 group-hover:translate-x-1">
                          <svg
                            className="inline h-3.5 w-3.5"
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M3 1.5l6 4.5-6 4.5V1.5z" />
                          </svg>
                        </span>
                      </h3>

                      <p className="mt-1 text-[13.5px] leading-[22px] text-[#64748B]">
                        {item.sub}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile / Tablet Navigation buttons */}
            <div className="mt-8 flex items-center justify-center gap-4 lg:hidden">
              <ArrowNavButton direction="left" onClick={handlePrevInsight} />
              <ArrowNavButton direction="right" onClick={handleNextInsight} />
            </div>
          </div>
        </section>

        <style>{`
          @keyframes marqueePartners {
            0% { transform: translateX(0%); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee-partners {
            display: flex;
            width: max-content;
            animation: marqueePartners 16s linear infinite;
          }
          .animate-marquee-partners:hover {
            animation-play-state: paused;
          }
        `}</style>

        {/* Technology Partners */}
        <section className="w-full border-b border-[#F1F5F9] bg-white py-14 overflow-hidden">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px] mb-8">
            <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#94A3B8]">
              TECHNOLOGY PARTNERS
            </p>
          </div>
          <div className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
            <div className="animate-marquee-partners items-center">
              {[...PARTNERS, ...PARTNERS, ...PARTNERS, ...PARTNERS].map((p, idx) => (
                <div key={`${p.name}-${idx}`} className="flex shrink-0 items-center justify-center px-10 sm:px-14">
                  <img 
                    src={p.img} 
                    alt={p.name} 
                    className="h-[28px] md:h-[34px] w-auto max-w-[150px] object-contain opacity-80 transition-all duration-300 hover:opacity-100 hover:scale-105" 
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter & Opportunity Index Section */}
        <section
          className="w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2 xl:gap-8">
              {/* Left Card: Newsletter */}
              <div
                style={{
                  backgroundColor: "rgba(245, 250, 254, 1)",
                  borderColor: "rgba(154, 197, 255, 0.7)",
                  borderRadius: "16px",
                  borderWidth: "1px",
                  borderStyle: "solid",
                }}
                className="flex flex-col justify-between p-7 sm:p-9 min-h-[260px] sm:min-h-[290px] shadow-[0_4px_24px_rgba(15,23,42,0.02)]"
              >
                <div>
                  <h3 className="text-[26px] sm:text-[30px] font-bold text-[#0F172A] tracking-tight">
                    Get insights in your inbox.
                  </h3>
                  <p className="mt-3 text-[14px] sm:text-[15px] leading-[22px] text-[#64748B] max-w-[460px]">
                    Join our newsletter for the latest research, perspectives and practical ideas on AI and the future of business.
                  </p>
                </div>

                <form
                  onSubmit={handleNewsletterSubmit}
                  className="mt-7 flex w-full max-w-[500px] items-center rounded-full bg-white p-1.5 border border-[#E2E8F0] shadow-sm focus-within:border-[#007AFF] transition-colors"
                >
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    className="w-full bg-transparent px-4 py-2 text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none"
                  />
                  <button
                    type="submit"
                    disabled={isSubscribing}
                    className="shrink-0 flex items-center gap-1.5 rounded-full bg-[#030B18] px-6 py-2.5 text-[13.5px] font-semibold text-white transition-all hover:bg-[#1E293B] disabled:opacity-60 cursor-pointer"
                  >
                    {isSubscribing ? (
                      "Subscribing..."
                    ) : (
                      <>
                        Subscribe <span aria-hidden="true">&rarr;</span>
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Right Card: Featured Resource */}
              <div
                style={{
                  background: "linear-gradient(135deg, rgba(18, 44, 78, 1) 0%, rgba(3, 11, 24, 1) 55%, rgba(1, 9, 25, 1) 100%)",
                  borderColor: "rgba(226, 232, 240, 0.7)",
                  borderRadius: "16px",
                  borderWidth: "1px",
                  borderStyle: "solid",
                }}
                className="relative flex flex-col justify-between overflow-hidden p-7 sm:p-9 min-h-[310px] shadow-[0_8px_30px_rgba(0,0,0,0.12)]"
              >
                <div className="relative z-10 max-w-[320px] sm:max-w-[360px]">
                  <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2px] text-[#8E9CAB]">
                    FEATURED RESOURCE
                  </p>
                  <h3 className="mt-2.5 text-[24px] sm:text-[28px] font-bold leading-[1.2] text-white">
                    The Enterprise AI Opportunity Index
                  </h3>
                  <p className="mt-3 text-[13.5px] sm:text-[14px] leading-[21px] text-[#94A3B8]">
                    A data-driven analysis of where the biggest AI opportunities lie across industries.
                  </p>
                </div>

                <div className="relative z-10 mt-6 sm:mt-7">
                  <a
                    href="/documents/Simpel-Magazine-2025.pdf"
                    download="Simpel final Magazine 2025.pdf"
                    onClick={() => toast.success("Downloading Simpel Company Report...")}
                    className="inline-flex items-center gap-2 rounded-full bg-white px-5 sm:px-6 py-2.5 sm:py-3 text-[13px] sm:text-[13.5px] font-semibold text-[#0F172A] transition-all hover:bg-slate-100 hover:scale-[1.02] shadow-sm cursor-pointer"
                  >
                    <span>Download Company Report</span>
                    <span aria-hidden="true">&rarr;</span>
                  </a>
                </div>

                {/* Booklet Image on Right */}
                <img
                  src="/images/insights/enterprise-opportunity-index.png"
                  alt="Enterprise AI Opportunity Index 2027"
                  className="absolute -right-6 sm:right-0 bottom-0 h-[240px] sm:h-[285px] lg:h-[308px] w-auto max-w-none object-contain pointer-events-none drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="cta" className="relative w-full overflow-hidden" style={{ minHeight: 320 }}>
          <img src="/images/labs/cta.png" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #04101D 0%, rgba(7,22,38,0.35) 55%, rgba(8,42,89,0) 100%)" }} />
          <div className="relative mx-auto max-w-[1440px] px-6 py-20 lg:px-[75px]">
            <div className="flex max-w-[600px] flex-col gap-4">
              <p className="text-[12px] font-semibold uppercase tracking-[3.36px] text-[#F8FBFF]">A MORE INTELLIGENT TOMORROW</p>
              <h2 className="text-[28px] font-semibold text-[#F8FBFF] sm:text-[32px]">Let’s build what’s next. Together.</h2>
              <p className="text-[14px] text-[#C9D3DF]">Partner with Simpel Labs to explore, co-create and scale AI innovations for your business.</p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => {
                    document.getElementById("labs")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="flex items-center gap-2 rounded-full bg-white px-6 py-3 text-[13px] font-medium text-black transition-transform hover:scale-[1.03] cursor-pointer"
                >
                  Explore Simpel Labs <ArrowRight className="h-4 w-4" />
                </button>
                <button onClick={openAgent} className="rounded-full border border-white px-6 py-3 text-[13px] font-medium text-white hover:bg-white/10 cursor-pointer">
                  Talk to an AI Strategy Expert
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SimpelLabs;